import { MediaData, TeamsData } from '../../assets/media';
import { enrichTeamMember } from './teamsMeta';

export const routes = {
  home: '/',
  about: '/about-us',
  fund: '/capri-stressed-assets-fund',
  teams: '/teams',
  teamMember: (slug) => `/teams/${slug}`,
  portfolio: '/portfolio',
  news: '/news-media',
  contact: '/contact',
  regulatory: '/regulatory',
  terms: '/terms-and-conditions',
  privacy: '/privacy-policy',
};

/** Fund page section anchors — linked from home strategy pillars. */
export const fundSections = {
  operate: 'how-we-operate',
  contribute: 'how-we-contribute',
  addValue: 'how-we-add-value',
};

export function fundSectionHref(sectionId) {
  return `${routes.fund}#${sectionId}`;
}

export const legalDocuments = {
  terms: {
    title: 'Terms and Conditions',
    heroTitle: ['Terms & ', { it: 'Conditions' }],
    file: '/documents/Terms-and-Conditions-CGIM-LLP.docx',
  },
  privacy: {
    title: 'Privacy Policy',
    heroTitle: ['Privacy ', { it: 'Policy' }],
    file: '/documents/Privacy-Policy-Sample-CGIM-LLP.docx',
  },
};

/** Standalone HTML uses `href="#"` for these — no pages; direct hits redirect home. */
export const placeholderLegalPaths = ['/fraud-alert'];

/** Standalone HTML hash routes (`#home`, `#about`, …) mapped to V2 paths. */
export const htmlLegacyRoutes = {
  home: routes.home,
  about: routes.about,
  fund: routes.fund,
  teams: routes.teams,
  portfolio: routes.portfolio,
  news: routes.news,
  contact: routes.contact,
  'team-rajesh': routes.teamMember('rajesh-sharma'),
  'team-sanjay': routes.teamMember('sanjay-jain'),
  'team-prs': routes.teamMember('pr-srinivasan'),
};

export function resolveHtmlLegacyRoute(key) {
  const normalized = (key ?? '').replace(/^#\/?/, '').trim().toLowerCase();
  if (!normalized) return null;
  return htmlLegacyRoutes[normalized] ?? null;
}

/** Office locations — Court Chambers first, Peninsula Business Park second. */
export const companyAddresses = [
  [
    '1B, 1st Floor, Court Chambers,',
    '35, Sir Vithaldas Thackersey Marg,',
    'New Marine Lines, Mumbai – 400020, Maharashtra, India',
  ],
  [
    '502, Tower A, Peninsula Business Park,',
    'Senapati Bapat Marg, Lower Parel,',
    'Mumbai – 400013, Maharashtra, India',
  ],
];

export const company = {
  nameTop: 'CÁPRI XPONENTIA',
  nameTopEmphasis: ['Á', 'XPONENTIA'],
  nameSub: 'MANAGERS LLP',
  location: 'Mumbai, India',
  phoneDisplay: '+91-22-40888100',
  phoneHref: 'tel:+912240888100',
  emailDisplay: 'support@capriglobalim.in',
  emailHref: 'mailto:support@capriglobalim.in',
  addresses: companyAddresses,
  mapEmbedUrl:
    'https://www.openstreetmap.org/export/embed.html?bbox=72.819%2C18.940%2C72.832%2C18.951&layer=mapnik&marker=18.9455%2C72.8255',
};

/** Header/footer shell copy from standalone HTML (distinct from page body branding). */
export const ui = {
  brandName: 'Capri Global Investment Managers LLP',
  utilityEmailDisplay: 'support@capriglobalim.in',
  utilityEmailHref: 'mailto:support@capriglobalim.in',
  footerEmailDisplay: 'support@capriglobalim.in',
};

/** Unique document titles per route (CIWX-103). */
const pageTitleByPath = {
  [routes.home]: 'Home',
  [routes.about]: 'About Us',
  [routes.fund]: 'Capri Stressed Assets Fund',
  [routes.teams]: 'Teams',
  [routes.portfolio]: 'Our Portfolio',
  [routes.news]: 'News and Media',
  [routes.contact]: 'Get In Touch',
  [routes.regulatory]: 'Regulatory',
  [routes.terms]: 'Terms and Conditions',
  [routes.privacy]: 'Privacy Policy',
};

export function pageTitleForPath(pathname) {
  if (pageTitleByPath[pathname]) return pageTitleByPath[pathname];
  if (pathname.startsWith(`${routes.teams}/`)) {
    const slug = pathname.slice(routes.teams.length + 1);
    const found = getTeamMemberBySlug(slug);
    if (found?.member?.name) return found.member.name;
    return 'Team Member';
  }
  return 'Home';
}

export function documentTitleForPath(pathname) {
  return `${pageTitleForPath(pathname)} | ${ui.brandName}`;
}

export const home = {
  badge: 'India-focused special situations investment platform · est. 2023',
  headlineLines: [
    ['Revitalising ', { it: 'Resilient' }],
    ['Businesses, Catalysing'],
    [{ it: 'Exponential' }, ' Growth.'],
  ],
  subhead:
    'India-focused special situations investment platform, designed to create value through significant equity-oriented structure.',
  stats: [
    { value: '2023', label: 'Launched' },
    { value: '₹750 ', valueUnit: 'Cr', valueSub: '(as on 30 Jun 2026)', label: 'Capital commitment' },
    { value: '100', valueEm: '+', suffix: ' Yrs', label: 'Leadership experience' },
  ],
};

export const about = {
  heroBadge: 'The House',
  heroTitle: ['About ', { it: 'Us' }],
  heroSub:
    'An India-focused special situations investment platform jointly promoted by the founders of the Capri Group and Xponentia Capital Partners.',
  intro:
    'Capri Global Investment Managers LLP (CGIM), formerly known as Capri Xponentia Managers LLP, is an India-focused investment platform jointly promoted by Mr Rajesh Sharma, the founder of the Capri Global Group and Mr PR Srinivasan and Mr Devinjit Singh, founders of Xponentia Capital Partners. Mr Sanjay Jain, ex CEO of Aditya Birla ARC Ltd, is being inducted as Managing Partner, subject to approval from SEBI. CGIM is Investment Manager to Capri Stressed Assets Fund (CSAF), a SEBI-registered Category II AIF.',
};

export const fund = {
  heroBadge: 'Flagship · SEBI Category II AIF',
  heroTitle: ['Capri Stressed ', { it: 'Assets Fund' }],
  heroSub:
    'Our fund investment thesis envisages participation in the value creation process while aiming to preserve the invested capital.',
};

export const teams = {
  heroBadge: 'The people behind the platform',
  heroTitle: ['Our ', { it: 'Teams' }],
  intro:
    'We are headed by an experienced team of professionals with a combined industry experience of more than 100 years. Their deep understanding of the Indian financial sector, as well as their experience in creating, turning around, and growing businesses in particular, is a key driver of the value creation process.',
  tabs: [
    { key: 'Leadership Teams', label: 'Leadership Teams' },
    { key: 'Investment Teams', label: 'Investment Teams' },
    { key: 'Operations Team', label: 'Operations Team' },
  ],
  data: TeamsData,
};

export const teamTabParams = {
  leadership: 'Leadership Teams',
  investment: 'Investment Teams',
  operations: 'Operations Team',
};

export function teamTabFromParam(param) {
  return teamTabParams[param] ?? teams.tabs[0].key;
}

export function teamTabToParam(tabKey) {
  const entry = Object.entries(teamTabParams).find(([, value]) => value === tabKey);
  return entry?.[0] ?? 'leadership';
}

export function teamsHref(tabKey = teams.tabs[0].key) {
  return `${routes.teams}?tab=${teamTabToParam(tabKey)}`;
}

export function teamMemberHref(slug, tabKey = teams.tabs[0].key) {
  return `${routes.teamMember(slug)}?tab=${teamTabToParam(tabKey)}`;
}

export function getTeamMemberBySlug(slug) {
  for (const [category, members] of Object.entries(TeamsData)) {
    const index = members.findIndex((m) => enrichTeamMember(m).slug === slug);
    if (index >= 0) {
      return { member: enrichTeamMember(members[index]), category, index };
    }
  }
  return null;
}

export function getEnrichedTeamsData() {
  const out = {};
  for (const [category, members] of Object.entries(TeamsData)) {
    out[category] = members.map(enrichTeamMember);
  }
  return out;
}

export const portfolio = {
  heroBadge: 'Investee Companies',
  heroTitle: ['Our ', { it: 'Portfolio' }],
  intro:
    'Capri Stressed Assets Fund partners with good businesses navigating capital challenges. Our current investee companies span India’s industrial manufacturing landscape.',
  companies: [
    {
      index: '01',
      sector: 'Industrial Fasteners',
      title: 'Deepak Fasteners Limited',
      titleSuffix: '(DFL)',
      description:
        'Deepak Fasteners Limited is the largest manufacturer of industrial high tensile fasteners in India. The company’s products are marketed and sold domestically and internationally under the world-renowned brand ',
      descriptionEmphasis: 'Unbrako',
      descriptionSuffix: '.',
      stats: [
        { label: 'Equity Investment', value: '₹375', unit: 'Cr' },
        { label: 'Debt Investment', value: '$48', unit: 'M' },
        { label: 'Flagship Brand', value: 'Unbrako' },
      ],
    },
    {
      index: '02',
      sector: 'Precision Casting',
      title: 'Indo Shell Mould Limited',
      description:
        'Indo Shell Mould Limited is among India’s leading shell moulding-based precision casting manufacturers. The Company provides end-to-end solutions from R&D and design to manufacturing and maintains long-standing relationships with marquee customers across automotive, hydraulics, and industrial segments.',
      chips: ['Automotive', 'Hydraulics', 'Industrial'],
    },
  ],
};

export const news = {
  heroBadge: 'Press & Insights',
  heroTitle: ['News & ', { it: 'Media' }],
  heroSub:
    'Check out news and insights about Capri Global Investment Managers LLP, and remain updated about the latest developments in the stressed asset segment.',
  items: MediaData,
};

export const contact = {
  heroBadge: 'Correspondence',
  heroTitle: ['Get In ', { it: 'Touch' }],
};

export const regulatory = {
  heroBadge: 'Compliance',
  heroTitle: ['Regulatory'],
  tableHeaders: ['Name of the Fund', 'Category of the Fund', 'Registration No.', 'Investment Manager'],
  funds: [
    {
      name: 'Capri India Investment Fund',
      category: 'Category-II',
      registrationNo: 'IN/AIF2/16-17/0251',
      manager: 'Capri Global Investment Managers LLP',
    },
    {
      name: 'Capri Special Situations Trust',
      category: 'Category-I',
      registrationNo: 'IN/AIF1/23-24/1291',
      manager: 'Capri Global Investment Managers LLP',
    },
    {
      name: 'Resilient India Investment Trust',
      category: 'Category-II',
      registrationNo: 'IN/AlF2/25-26/1789',
      manager: 'Capri Global Investment Managers LLP',
    },
  ],
  investorHeading: 'For Investors in Capri Global Investment Managers Ltd. Opportunities Funds ONLY',
  supportEmailDisplay: 'support@capriglobalim.in',
  odrUrl: 'https://smartodr.in/login',
};
